'use client';

import { matchesControllerGetMatchDetails } from "@/utils/api/sdk.gen";
import { rewardsBlockItem, rewardsBlockItemDataFragmentDoc } from "@gql/graphql";
import type { DocumentNode } from 'graphql';
import dynamic from "next/dynamic";
import router from "next/dist/client/router";
import { useEffect, useState } from "react";

// Dynamically import MatchCard (no SSR)
const MatchCard = dynamic(
  () => import("@nrldigitaltechnology/ui-community").then(mod => mod.MatchCard),
  { ssr: false }
);

type RewardsBlockItemProps = {
  data: rewardsBlockItem;
  inEditMode?: boolean;
};
type Address = {
  formatted: string;
  lat: number;
  lng: number;
  addressLine1: string;
  addressLine2: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
} | {
  formatted?: string;
  lat?: number;
  lng?: number;
};

interface RewardsBlockItemComponent extends React.FC<RewardsBlockItemProps> {
  getDataFragment: () => [string, DocumentNode];
}

type BadgeProps = { id: string; label: string; shortLabel?: string };
type RewardProps = { ctaTxt: string; ctaUrl: string };
type CustomStyles = { [key: string]: string };
type TeamProps = { id: string; name: string; score: number; logo: string; forfeit?: boolean, avatar?: string; };

type scores = {
  homeTeam: number,
  awayTeam: number
}

interface MatchCardDataProps {
  id: string;
  round?: string;
  homeTeam: TeamProps;
  awayTeam: TeamProps;
  status?: BadgeProps;
  liveUrl?: string;
  venue?: string;
  fullVenue?: {address: Address};
  dateTime?: string;
  byes?: boolean;
  period?: string;
  hideShare?: boolean;
  customStyles?: CustomStyles;
  reward?: RewardProps;
  alternateSmMob?: boolean;
  primaryBtnLbl?: string;
  secondaryBtnLbl?: string;
}

interface MatchCardResponseProps {
  id: string;
  round?: {
    type: string,
    displayName: string,
    number: number
  },
  homeTeam: TeamProps;
  awayTeam: TeamProps;
  status?: string;
  liveUrl?: string;
  venue?:  {
    _id: number,
    name: string,
    venueTimezone: number
  },
  fullVenue?: {address: Address};
  dateTime?: string;
  scores?: scores;
  byes?: boolean;
  period?: string;
  hideShare?: boolean;
  customStyles?: CustomStyles;
  reward?: RewardProps;
  alternateSmMob?: boolean;
  primaryBtnLbl?: string;
  secondaryBtnLbl?: string;
}

const statusMapper: BadgeProps[] = [
  {
    id: "live",
    label: "LIVE",
  },
  {
    id: "washout",
    label: "WASHOUT",
    shortLabel: "WASH",
  },
  {
    id: "postponed",
    label: "POSTPONED",
    shortLabel: "POST",
  },
  {
    id: "forfeit",
    label: "FORFEIT",
  },
  {
    id: "tba",
    label: "TBA",
  },
  {
    id: "fulltime",
    label: "FULLTIME",
    shortLabel: "FT",
  },
];

const RewardsBlockItem: RewardsBlockItemComponent = ({ data }) => {
  const { matchId, callToAction, ctaUrl } = data || {};
  const matchIdStr = matchId ? String(matchId) : undefined;

  const [matchData, setMatchData] = useState<Partial<MatchCardResponseProps>>({});
  const [matchLoading, setMatchLoading] = useState(false);

  useEffect(() => {
    if (!matchIdStr) return;

    const fetchMatchData = async () => {
      setMatchLoading(true);
      const isReward = callToAction ? true : false;
      try {
        const response = await matchesControllerGetMatchDetails({ 
          path: { id: matchIdStr }, 
          query: { isReward: isReward }
        });
        if (response?.data) {
          setMatchData(response.data as MatchCardResponseProps); // Cast the response data to MatchCardResponseProps
        }
      } catch (err) {
        console.error("Error fetching match data:", err);
      } finally {
        setMatchLoading(false);
      }
    };

    fetchMatchData();
  }, [matchIdStr]);

  // Build the card data with safe defaults
  const cardData: MatchCardDataProps = {
    id: matchData.id ?? "",
    round: matchData?.round?.displayName ?? "",
    homeTeam: {
      id: matchData.homeTeam?.id ?? "",
      name: matchData.homeTeam?.name ?? "m",
      score: matchData.scores?.homeTeam ?? 0,
      logo: matchData.homeTeam?.avatar ?? "", // Provide a default value here,
      forfeit: matchData.homeTeam?.forfeit ?? false,
    },
    awayTeam: {
      id: matchData.awayTeam?.id ?? "",
      name: matchData.awayTeam?.name ?? "",
      score: matchData.awayTeam?.score ?? 0,
      logo: matchData.awayTeam?.avatar ?? "",
      forfeit: matchData.awayTeam?.forfeit ?? false,
    },
    status: (matchData?.status === ""
      ? statusMapper.find((it: BadgeProps) => it.id === "")
      : { id: "", label: "" }),
    // liveUrl: matchData.meta?.liveUrl || "",
    venue: matchData?.venue?.name ?? "",
    fullVenue: {address: matchData?.fullVenue?.address ?? {}},
    dateTime: matchData.dateTime ?? "",
    // byes: matchData.byes ?? false,
    period: "",//matchData.period ?? "",
    hideShare: matchData.hideShare ?? false,
    // customStyles: matchData.customStyles,
    reward: {
      ctaTxt: callToAction ?? "",
      ctaUrl: (ctaUrl?.default as string) ?? "",
    },
    // alternateSmMob: matchData.alternateSmMob ?? false,
    // primaryBtnLbl: matchData.primaryBtnLbl ?? "",
    // secondaryBtnLbl: matchData.secondaryBtnLbl ?? "",
  };

  if (matchLoading) return <div>Loading match data...</div>;

  const matchClick = (
    cardData: MatchCardDataProps | undefined
  ) => {
    if (!cardData) return;
    {
      if (cardData?.fullVenue?.address) {
        const address = cardData.fullVenue.address;
        let queryStr = `query=${cardData?.venue?.replace(/\s+/g, "+")}`;
        if (address.formatted)
          queryStr = queryStr + `,${address.formatted.replace(/\s+/g, "+")}`;

        const mapsStr = `https://www.google.com/maps/search/?api=1&${queryStr}`;
        window.open(mapsStr, "_blank")?.focus();
      }
    }
  };

  return (
    <div className="rewards-block-item">
      {cardData && (
        <MatchCard
          cardData={cardData}
          onClickCard={() => router.push(`/competitions/match/${cardData.id}?type=match`)}
          onClickVenue={() => matchClick(cardData)}
          onClickShare={() => console.log("share match details")}
        />
      )}
    </div>
  );
};

RewardsBlockItem.getDataFragment = (): [string, DocumentNode] => [
  "rewardsBlockItemData",
  rewardsBlockItemDataFragmentDoc,
];

export default RewardsBlockItem;
